const dangerousProtocolPattern = /^\s*(javascript|data|vbscript)\s*:/i;

export function isSafeRedirectUrl(url: string, currentOrigin?: string): boolean {
    if (!url) {
        return false;
    }

    const decoded = decodeURIComponent(url);

    if (dangerousProtocolPattern.test(decoded)) {
        return false;
    }

    if (decoded[0] === "/") {
        if (decoded[1] === "/" || decoded[1] === "\\" || (decoded.length > 1 && decoded.charCodeAt(1) <= 0x20)) {
            return false;
        }
        return true;
    }

    if (currentOrigin && (decoded.startsWith("http://") || decoded.startsWith("https://"))) {
        try {
            const parsedUrl = new URL(decoded);
            return parsedUrl.origin === currentOrigin;
        } catch {
            return false;
        }
    }

    return false;
}

interface WindowLike {
    location: { href: string; origin: string };
}

export function safeRedirect(url: string, fallbackUrl?: string, windowOverride?: WindowLike): void {
    const w = windowOverride ?? window;
    const origin = typeof w !== "undefined" ? w.location.origin : undefined;
    if (isSafeRedirectUrl(url, origin)) {
        w.location.href = url;
    } else if (fallbackUrl && isSafeRedirectUrl(fallbackUrl, origin)) {
        w.location.href = fallbackUrl;
    } else {
        // eslint-disable-next-line no-console
        console.error("Could not redirect to " + url + " or " + fallbackUrl + " because neither is considered safe.");
    }
}

export function isAllowedRedirectHost(url: string, requestHost: string, allowedHosts: readonly string[]): boolean {
    if (!url) {
        return false;
    }

    let decoded: string;
    try {
        decoded = decodeURIComponent(url);
    } catch {
        return false;
    }

    if (dangerousProtocolPattern.test(decoded)) {
        return false;
    }

    const lower = decoded.toLowerCase();
    if (lower.startsWith("http:") || lower.startsWith("https:")) {
        let parsed: URL;
        try {
            parsed = new URL(decoded);
        } catch {
            return false;
        }

        const targetHost = parsed.hostname.toLowerCase();
        return (
            targetHost === requestHost.toLowerCase() ||
            allowedHosts.some(allowed => allowed.toLowerCase() === targetHost)
        );
    }

    if (decoded[0] === "/") {
        if (decoded[1] === "/" || decoded[1] === "\\" || (decoded.length > 1 && decoded.charCodeAt(1) <= 0x20)) {
            return false;
        }
        return true;
    }

    return false;
}
